import { ApiError, getHttpErrorMessage } from '@fondationcss/sdk';
import { useSnackbar } from '@fondationcss/ui';

type SdkLike = {
  fetchWrapper: { setDefaultErrorHandler: (handler: (error: unknown) => void) => void };
};

/**
 * Offline support (#377): while the device is offline the app serves cached data, but the
 * Fondation activity heartbeat and any background fetch keep failing — which would otherwise
 * spam the generic "erreur réseau" snackbar throughout an offline session (a teacher running a
 * course in the field for hours).
 *
 * This installs a default fetch error handler that stays silent while offline *and* on a page
 * that opts in (via {@link isOfflinePage}) and, otherwise, behaves exactly as before: it wraps the
 * framework's existing handler when reachable (preserving any auth / logging side effects),
 * otherwise falls back to the app's standard error snackbar.
 *
 * Scoping keeps the blast radius tight: only the offline-capable Présences page swallows network
 * errors while offline; every other page keeps reporting them as usual.
 *
 * Call once, after `app.mount()`, so it takes precedence over the handler the root installs on
 * mount. Online behaviour is unchanged everywhere.
 *
 * @param isOfflinePage Returns true when the current route is an offline-capable page.
 */
export function installOfflineErrorSuppression(sdk: SdkLike, isOfflinePage: () => boolean): void {
  try {
    // The framework keeps its default handler on the (internal) state ref; wrapping it preserves
    // every online side effect. If it isn't reachable, fall back to the standard app snackbar.
    const internal = sdk as unknown as { defaultErrorHandler?: { value: ((error: unknown) => void) | null } };
    const original = internal.defaultErrorHandler?.value ?? null;

    let fallback: ((error: unknown) => void) | null = null;
    if (!original) {
      try {
        const { showError } = useSnackbar();
        fallback = (error: unknown) => {
          const reason = error instanceof ApiError
            ? (error.content?.title ?? (error.httpStatus ? getHttpErrorMessage(error.httpStatus) : 'erreur réseau'))
            : null;
          showError(reason ? `La requête a échoué : ${reason}` : 'Erreur inattendue');
        };
      } catch {
        // Snackbar unavailable here — leave the framework default in place rather than risk
        // silencing online errors too.
      }
    }

    const delegate = original ?? fallback;
    if (!delegate) return;

    sdk.fetchWrapper.setDefaultErrorHandler((error: unknown) => {
      const offline = typeof navigator !== 'undefined' && !navigator.onLine;
      if (offline && isOfflinePage()) return;
      delegate(error);
    });
  } catch (e) {
    console.warn('Gestionnaire d\'erreur hors-ligne non installé', e);
  }
}
